/**
 * Phase 62 Plan 62-08 — Locale-stable month+year picker.
 *
 * Two native <select> controls (Month / Year) with English labels hardcoded.
 * Built because <input type="month"> leaks browser-locale month names on
 * Czech machines (Petra UAT 2026-06-10 — Phase 62 rejected the native
 * control for the same reason as Phase 48/50 rejected <input type="date">).
 *
 * Used by InvoicePendingPage and ToBeInvoicedCard (both To Be Invoiced surfaces).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

function buildYears(): number[] {
  const now = new Date().getUTCFullYear();
  // current year +1 down to current year -6 (8 years total, matching EntriesNeedingRolePage range)
  return Array.from({ length: 8 }, (_, i) => now + 1 - i);
}

function parseValue(value: string): { month: string; year: string } {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return { month: '', year: '' };
  return {
    year: m[1] ?? '',
    month: m[2] ?? '',
  };
}

function buildValue(month: string, year: string): string {
  if (!month || !year) return '';
  return `${year}-${month}`;
}

export interface MonthYearPickerProps {
  /** YYYY-MM or empty string */
  value: string;
  /** Fires with YYYY-MM when both selects have a value; empty string otherwise. */
  onChange: (next: string) => void;
  /** Accessible label used as the <fieldset> aria-label / legend. */
  label?: string;
  /** Optional id prefix for the two select elements (a11y). Default: 'my' */
  idPrefix?: string;
  disabled?: boolean;
}

export function MonthYearPicker({
  value,
  onChange,
  label,
  idPrefix = 'my',
  disabled = false,
}: MonthYearPickerProps) {
  const nowYear = new Date().getUTCFullYear();
  const years = useMemo(buildYears, [nowYear]);

  // Internal state tracks both parts independently.
  const [parts, setParts] = useState(() => parseValue(value));

  // Sync internal state when the controlled value changes from outside.
  useEffect(() => {
    setParts(parseValue(value));
  }, [value]);

  const handleChange = useCallback(
    (field: 'month' | 'year', fieldValue: string) => {
      const next = { ...parts, [field]: fieldValue };
      setParts(next);
      onChange(buildValue(next.month, next.year));
    },
    [parts, onChange],
  );

  return (
    <fieldset className="inline-flex items-center gap-1" aria-label={label}>
      {label && <legend className="sr-only">{label}</legend>}
      <select
        id={`${idPrefix}-month`}
        value={parts.month}
        disabled={disabled}
        onChange={(e) => handleChange('month', e.target.value)}
        className="border border-gray-300 rounded px-2 py-1 text-sm"
        aria-label="Month"
      >
        <option value="">—</option>
        {MONTH_NAMES.map((name, idx) => {
          const mm = String(idx + 1).padStart(2, '0');
          return (
            <option key={mm} value={mm}>
              {name}
            </option>
          );
        })}
      </select>
      <select
        id={`${idPrefix}-year`}
        value={parts.year}
        disabled={disabled}
        onChange={(e) => handleChange('year', e.target.value)}
        className="border border-gray-300 rounded px-2 py-1 text-sm"
        aria-label="Year"
      >
        <option value="">—</option>
        {years.map((y) => (
          <option key={y} value={String(y)}>
            {y}
          </option>
        ))}
      </select>
    </fieldset>
  );
}
